/*
 * Cammarata Mode — public Shopify price/compare-at loader.
 * Install this Shopify theme asset ONCE with:
 * <script type="module" src="{{ 'cammarata-seo-prices.js' | asset_url }}"></script>
 * Place data-cm-price-for="<shopify-product-handle>" on the price of each SEO product card.
 *
 * No app, no token, no Vercel, no discount assumptions.
 * Shopify automatic cart discounts CANNOT be read via this endpoint.
 */
const handlePattern = /^[a-z0-9][a-z0-9_-]{0,199}$/i;

export function derivePublicPrice(product) {
  if (!product || !Array.isArray(product.variants) || !product.variants.length) return null;
  const variants = product.variants.filter(v =>
    v && v.available === true && Number.isSafeInteger(v.price) && v.price >= 0
  );
  if (!variants.length) return { available: false };
  // Shopify's Ajax response caps variants at 250. Never promise a sale
  // based on an incomplete set of variants when there may be more.
  const potentiallyTruncated = product.variants.length >= 250;
  const minPrice = Math.min(...variants.map(v => v.price));
  const maxPrice = Math.max(...variants.map(v => v.price));
  const cheapest = variants.filter(v => v.price === minPrice);
  let compareAtPrice = null;
  if (!potentiallyTruncated && cheapest.length) {
    const compareCandidates = cheapest.map(v => v.compare_at_price);
    if (compareCandidates.every(v =>
      Number.isSafeInteger(v) && v > minPrice && v === compareCandidates[0])) {
      compareAtPrice = compareCandidates[0];
    }
  }
  const salePercent = compareAtPrice !== null
    ? Math.round(100 * (compareAtPrice - minPrice) / compareAtPrice)
    : null;
  return {
    available: true,
    priceCents: minPrice,
    compareAtPriceCents: compareAtPrice,
    salePercent,
    variesByAvailableSize: maxPrice > minPrice,
    source: 'shopify_ajax_product',
  };
}

function currencyFormatter() {
  const active = globalThis.Shopify?.currency?.active;
  const currency = typeof active === 'string' && /^[A-Z]{3}$/.test(active) ? active : 'EUR';
  return new Intl.NumberFormat('it-IT', { style: 'currency', currency });
}

function addSlots(cards, handle) {
  // Future pages generated by Shopify/Codex may use the same markup;
  // existing K-Way page already has these slots.
  return {
    price: cards.querySelectorAll('[data-cm-price-for="' + handle + '"]'),
    original: cards.querySelectorAll('[data-cm-price-base-for="' + handle + '"]'),
    badge: cards.querySelectorAll('[data-cm-discount-card="' + handle + '"] [data-cm-discount-percent]'),
  };
}

function updateText(nodes, value, visible) {
  for (const el of nodes) {
    if (value !== null) el.textContent = value;
    el.hidden = !visible;
    el.style.display = visible ? '' : 'none';
  }
}

function rootPath() {
  const root = globalThis.Shopify?.routes?.root;
  if (typeof root !== 'string' || !root.startsWith('/') || root.startsWith('//')) return '/';
  return root.endsWith('/') ? root : root + '/';
}

export async function hydrateSeoPrices(context = document, request = fetch) {
  const slots = [...context.querySelectorAll('[data-cm-price-for]')];
  const handles = [...new Set(slots.map(node => node.getAttribute('data-cm-price-for'))
    .filter(handle => handle && handlePattern.test(handle)))];
  if (!handles.length) return { total: 0, updated: 0 };

  // At most 5 AJAX requests at a time. Failures never create fictitious sales.
  const formatter = currencyFormatter();
  let next = 0;
  let updated = 0;
  async function worker() {
    while (next < handles.length) {
      const handle = handles[next++];
      const card = addSlots(context, handle);
      // Remove any previous sale badge before network refresh.
      updateText(card.badge, null, false);
      updateText(card.original, null, false);
      try {
        const res = await request(rootPath() + 'products/' + encodeURIComponent(handle) + '.js', {
          method: 'GET', credentials: 'same-origin', cache: 'no-store',
        });
        if (!res.ok) continue;
        const price = derivePublicPrice(await res.json());
        if (!price) continue;
        if (!price.available) {
          updateText(card.price, 'Non disponibile', true);
          updated++;
          continue;
        }
        const from = price.variesByAvailableSize ? 'Da ' : '';
        updateText(card.price, from + formatter.format(price.priceCents / 100), true);
        if (price.compareAtPriceCents !== null && price.salePercent !== null) {
          updateText(card.original, formatter.format(price.compareAtPriceCents / 100), true);
          updateText(card.badge, '-' + price.salePercent + '% sul prodotto', true);
        }
        updated++;
      } catch {
        // Keep the rendered/base price if Shopify Ajax is unavailable.
      }
    }
  }
  await Promise.all(Array.from({ length: Math.min(5, handles.length) }, () => worker()));
  return { total: handles.length, updated };
}

if (typeof document !== 'undefined') {
  const run = () => { void hydrateSeoPrices(); };
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', run, { once: true });
  } else run();
  document.addEventListener('shopify:section:load', run);
}
